import { Link } from 'react-router';

import { AdminUrls, useAdminBreadcrumbLastLabel } from '@treneo/features/admin';
import { useAppTranslations } from '@treneo/shared/i18n';
import { useAppLocation } from '@treneo/shared/router';
import { Breadcrumbs, styled, Typography } from '@treneo/shared/ui';

const StyledBreadcrumbs = styled(Breadcrumbs)`
  max-width: 100%;
  overflow: hidden;

  & .MuiBreadcrumbs-li {
    min-width: 0;
    overflow: hidden;

    &:not(:last-child) {
      flex-shrink: 1;
    }

    &:last-child {
      flex-shrink: 0;
    }
  }
`;

const BreadcrumbTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== '$isLast'
})<{ $isLast?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  ${({ $isLast }) => $isLast && 'font-weight: 700 !important;'}
`;

type Breadcrumb = {
  label: string;
  path: string;
};

const getAdminBreadcrumbs = (
  pathname: string,
  t: (key: string) => string,
  lastLabelOverride: string | null
): Breadcrumb[] => {
  const breadcrumbs: Breadcrumb[] = [{ label: t('admin.adminPanel'), path: AdminUrls.adminPanel }];

  if (pathname === AdminUrls.health) {
    breadcrumbs.push({ label: t('admin.healthCheck'), path: AdminUrls.health });
  } else if (pathname === AdminUrls.organizations) {
    breadcrumbs.push({
      label: t('admin.organizationsLabel'),
      path: AdminUrls.organizations
    });
  } else if (pathname.startsWith(`${AdminUrls.organizations}/`) && pathname.includes('/users')) {
    breadcrumbs.push(
      {
        label: t('admin.organizationsLabel'),
        path: AdminUrls.organizations
      },
      {
        label: t('admin.organizationsMenu.users'),
        path: pathname
      }
    );
  } else if (
    pathname.startsWith(`${AdminUrls.organizations}/`) &&
    pathname.includes('/invitations')
  ) {
    breadcrumbs.push(
      {
        label: t('admin.organizationsLabel'),
        path: AdminUrls.organizations
      },
      {
        label: t('admin.organizationsMenu.invitations'),
        path: pathname
      }
    );
  } else if (pathname.startsWith(`${AdminUrls.permissions}/`)) {
    const lastLabel =
      lastLabelOverride != null && lastLabelOverride !== ''
        ? lastLabelOverride
        : t('admin.permission');
    breadcrumbs.push(
      { label: t('admin.permissions'), path: AdminUrls.permissions },
      { label: lastLabel, path: pathname }
    );
  } else if (pathname === AdminUrls.permissions) {
    breadcrumbs.push({ label: t('admin.permissions'), path: AdminUrls.permissions });
  } else if (pathname.startsWith(`${AdminUrls.users}/`)) {
    const lastLabel =
      lastLabelOverride != null && lastLabelOverride !== '' ? lastLabelOverride : t('admin.user');
    breadcrumbs.push(
      { label: t('admin.users'), path: AdminUrls.users },
      { label: lastLabel, path: pathname }
    );
  } else if (pathname === AdminUrls.users) {
    breadcrumbs.push({ label: t('admin.users'), path: AdminUrls.users });
  } else if (pathname === AdminUrls.loginActivity) {
    breadcrumbs.push({
      label: t('admin.loginActivity.menuItem'),
      path: AdminUrls.loginActivity
    });
  } else if (pathname === AdminUrls.invitations) {
    breadcrumbs.push({
      label: t('admin.invitationsLabel'),
      path: AdminUrls.invitations
    });
  } else if (pathname === AdminUrls.cache) {
    breadcrumbs.push({ label: t('admin.cache.title'), path: AdminUrls.cache });
  } else if (pathname === AdminUrls.synchronization) {
    breadcrumbs.push({
      label: t('admin.syncPage.title'),
      path: AdminUrls.synchronization
    });
  }

  return breadcrumbs;
};

export const AdminBreadcrumbs = () => {
  const { t } = useAppTranslations();
  const location = useAppLocation();
  const lastLabelOverride = useAdminBreadcrumbLastLabel();
  const breadcrumbs = getAdminBreadcrumbs(location.pathname, t, lastLabelOverride);

  if (breadcrumbs.length <= 1) {
    return null;
  }

  const renderedBreadcrumbs = breadcrumbs.map((breadcrumb, index) => {
    const isCurrentPage = breadcrumb.path === location.pathname;
    const content = (
      <BreadcrumbTitle $isLast={isCurrentPage} variant='body2'>
        {breadcrumb.label}
      </BreadcrumbTitle>
    );

    if (isCurrentPage) {
      return <div key={`${breadcrumb.path}-${index}`}>{content}</div>;
    }

    return (
      <Link
        key={`${breadcrumb.path}-${index}`}
        to={breadcrumb.path}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {content}
      </Link>
    );
  });

  return <StyledBreadcrumbs>{renderedBreadcrumbs}</StyledBreadcrumbs>;
};
